import { useState, useEffect } from "react";
import { faDatabase, faSpinner, faGlobe, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Only render in development - check once at module level
const isDevelopment = process.env.NODE_ENV === "development";

const DevToolbar = () => {
  const host = process.env.REACT_APP_SERVER;
  const [databaseInfo, setDatabaseInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState(null);
  const [proxyStatus, setProxyStatus] = useState(null);
  const [showProxyDetails, setShowProxyDetails] = useState(false);

  // Fetch current database info
  useEffect(() => {
    // Skip fetching in production
    if (!isDevelopment) return;
    const fetchDatabaseInfo = async () => {
      try {
        const response = await fetch(`${host}/api/dev/database`);
        if (response.ok) {
          const data = await response.json();
          setDatabaseInfo(data.data);
        } else {
          // Endpoint doesn't exist (prod backend) - silently disable
          setDatabaseInfo(null);
        }
      } catch (err) {
        // Backend not available - silently disable
        setDatabaseInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDatabaseInfo();
  }, [host]);

  // Fetch proxy status
  useEffect(() => {
    if (!isDevelopment) return;

    const fetchProxyStatus = async () => {
      try {
        const response = await fetch(`${host}/api/admin/proxy-status`);
        if (response.ok) {
          const data = await response.json();
          setProxyStatus(data);
        }
      } catch (err) {
        setProxyStatus({ status: "error", message: "Failed to fetch" });
      }
    };

    fetchProxyStatus();
    // Refresh proxy status every 5 minutes
    const interval = setInterval(fetchProxyStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [host]);

  const handleDatabaseSwitch = async (newDatabase) => {
    if (newDatabase === databaseInfo?.active) return;

    setIsSwitching(true);
    setError(null);

    try {
      const response = await fetch(`${host}/api/dev/database`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ database: newDatabase }),
      });

      if (response.ok) {
        const data = await response.json();
        setDatabaseInfo(data.data);
        // Reload the page to refresh data with new database
        window.location.reload();
      } else {
        const data = await response.json();
        setError(data.message || "Failed to switch database");
      }
    } catch (err) {
      setError("Failed to connect to backend");
    } finally {
      setIsSwitching(false);
    }
  };

  // Don't render in production or if endpoint not available
  if (!isDevelopment || (!isLoading && !databaseInfo)) {
    return null;
  }

  const isProd = databaseInfo?.active === "prod";

  return (
    <div
      className={`fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg backdrop-blur-sm text-xs font-mono transition-all ${
        isProd
          ? "bg-red-900/90 text-red-100 border border-red-500/50"
          : "bg-black/70 text-white/80 border border-white/10"
      }`}
    >
      <FontAwesomeIcon
        icon={faDatabase}
        className={isProd ? "text-red-300" : "text-white/50"}
      />

      {isLoading ? (
        <FontAwesomeIcon icon={faSpinner} spin />
      ) : error ? (
        <span className="text-red-400">{error}</span>
      ) : (
        <>
          <select
            className={`bg-transparent border-none outline-none cursor-pointer font-mono text-xs ${
              isProd ? "text-red-100" : "text-white/90"
            }`}
            value={databaseInfo?.active || ""}
            onChange={(e) => handleDatabaseSwitch(e.target.value)}
            disabled={isSwitching}
          >
            {databaseInfo?.available?.map((db) => (
              <option key={db} value={db} className="bg-gray-900 text-white">
                {db.toUpperCase()}
              </option>
            ))}
          </select>

          {isSwitching && (
            <FontAwesomeIcon icon={faSpinner} spin />
          )}

          {isProd && (
            <span className="text-red-300 font-bold animate-pulse">PROD</span>
          )}

          {/* Proxy Status Indicator */}
          {proxyStatus && (
            <>
              <span className="mx-1 text-white/20">|</span>
              <div className="relative">
                <button
                  onClick={() => setShowProxyDetails(!showProxyDetails)}
                  className={`flex items-center gap-1.5 hover:opacity-80 ${
                    proxyStatus.status === "ok"
                      ? "text-green-400"
                      : proxyStatus.status === "not_configured"
                      ? "text-yellow-400"
                      : "text-red-400"
                  }`}
                  title={proxyStatus.status === "ok" ? "Proxy OK" : proxyStatus.message || "Proxy issue"}
                >
                  <FontAwesomeIcon
                    icon={proxyStatus.status === "ok" ? faGlobe : faExclamationTriangle}
                    className="text-[10px]"
                  />
                  <span className="text-[10px]">
                    {proxyStatus.status === "ok" ? "Proxy" : proxyStatus.status === "not_configured" ? "No Proxy" : "Proxy!"}
                  </span>
                </button>

                {/* Proxy Details Popup */}
                {showProxyDetails && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowProxyDetails(false)} />
                    <div className="absolute bottom-full left-0 mb-2 p-3 bg-gray-900 rounded-lg shadow-xl border border-white/10 min-w-[200px] z-50">
                      <h4 className="text-xs font-bold text-white mb-2">Proxy Status</h4>
                      <div className="space-y-1 text-[10px]">
                        <div className="flex justify-between">
                          <span className="text-white/50">Status:</span>
                          <span className={proxyStatus.status === "ok" ? "text-green-400" : "text-red-400"}>
                            {proxyStatus.status}
                          </span>
                        </div>
                        {proxyStatus.username && (
                          <div className="flex justify-between">
                            <span className="text-white/50">User:</span>
                            <span className="text-white/80">{proxyStatus.username}</span>
                          </div>
                        )}
                        {proxyStatus.host && (
                          <div className="flex justify-between">
                            <span className="text-white/50">Host:</span>
                            <span className="text-white/80">{proxyStatus.host}</span>
                          </div>
                        )}
                        {proxyStatus.data && (
                          <>
                            {proxyStatus.data.traffic_used !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-white/50">Traffic:</span>
                                <span className="text-white/80">
                                  {(proxyStatus.data.traffic_used / 1024 / 1024 / 1024).toFixed(2)} GB
                                </span>
                              </div>
                            )}
                            {proxyStatus.data.requests !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-white/50">Requests:</span>
                                <span className="text-white/80">{proxyStatus.data.requests.toLocaleString()}</span>
                              </div>
                            )}
                          </>
                        )}
                        {proxyStatus.message && proxyStatus.status !== "ok" && (
                          <div className="mt-2 pt-2 border-t border-white/10 text-red-400">
                            {proxyStatus.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DevToolbar;
